import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type RejectionReasonDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  reason: string
  onReasonChange: (reason: string) => void
  rejectMode: boolean
}

export const RejectionReasonDialog = ({
  open,
  onClose,
  onConfirm,
  reason,
  onReasonChange,
  rejectMode,
}: RejectionReasonDialogProps) => {
  const { t } = useTranslation('requests')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    // Settle (success or the caller's own error snackbar) then close, so a retry can't re-submit.
    await Promise.resolve(onConfirm()).catch(() => undefined)
    setIsSubmitting(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) {
          onClose()
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>{rejectMode ? t('rejection.reject-request') : t('rejection.reopen-request')}</DialogTitle>
      <DialogContent sx={{ gap: 4 }}>
        <DialogContentText>{rejectMode ? t('rejection.dialog-body') : t('rejection.dialog-reopen-body')}</DialogContentText>
        <TextField
          label={rejectMode ? t('rejection.dialog-label') : t('rejection.dialog-reopen-label')}
          multiline
          rows={5}
          sx={{ width: '100%' }}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('rejection.cancel')}
        </Button>
        {rejectMode ? (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={reason.trim() === ''}
            loading={isSubmitting}
          >
            {t('rejection.reject')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={reason.trim() === ''}
            loading={isSubmitting}
          >
            {t('rejection.confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
